import { useState, useEffect } from "react";
import {
  validatePincode,
  validateBudget,
  validateWebsite,
  validateNumberWithLimit,
 
} from "../../utils/validation";


import {
  formatDateAPIFormat,
  convertBudgetToNumber,
  splitBudgetIntoValueAndUnit,
  parseDateString,
} from "../../hooks/commonHooks";

import { UNIT_TYPES } from "../../data/intialData";

const ALL_UNIT_TYPES_OPTION = "all";

const createInitialFormData = () => ({
  name: "",
  city: "",
  area: "",
  state: "",
  country: "India",
  pincode: "",
  site_property_url: "",
  property_type: "",
  possession_status: "ongoing",
  unit_type: [],
  min_budget: "",
  max_budget: "",
  expected_delivery: "",
  status: "active",
  brochure: null,
  image: null,
});

export const usePropertyForm = (initialData = {}, onSubmit, onCancel) => {
  const [showCalendar, setShowCalendar] = useState(false);

  const [formData, setFormData] = useState(() => createInitialFormData());

  const [minBudgetUnitType, setMinBudgetUnitType] = useState("");
  const [maxBudgetUnitType, setMaxBudgetUnitType] = useState("");

  const [errors, setErrors] = useState({});
  const isEditMode = !!initialData?.id;

  // ✅ Load edit data
  useEffect(() => {
    if (initialData?.id) {
      const min = splitBudgetIntoValueAndUnit(initialData.min_budget);
      const max = splitBudgetIntoValueAndUnit(initialData.max_budget);
      setMinBudgetUnitType(min.unit);
      setMaxBudgetUnitType(max.unit);
      setFormData({
        name: initialData.title || "",
        city: initialData.city || "",
        area: initialData.area || "",
        state: initialData.state || "",
        country: initialData.country || "",
        pincode: initialData.pincode || "",
        site_property_url: initialData.property_url || "",
        property_type: initialData.property_type || "",
        possession_status: initialData.possession_status || "ongoing",
        unit_type: Array.isArray(initialData.unit_type)
          ? initialData.unit_type
          : [],
        min_budget: min.value || "",
        max_budget: max.value || "",
        expected_delivery:
          parseDateString(initialData.expected_delivery) || "",
        status: initialData.status || "active",
        brochure: initialData.brochure_url || initialData.brochure || null,
        image: initialData.image_url || initialData.image || null,
      });
    }
  }, [initialData]);

  // ✅ CHANGE
  const handleChange = (name, value) => {
    if (name === "pincode" && !validateNumberWithLimit(6)(value)) return;
    if ((name === "min_budget" || name === "max_budget") && !validateBudget(value)) return;

    const normalizedValue =
      name === "unit_type" && Array.isArray(value)
        ? value.includes(ALL_UNIT_TYPES_OPTION)
          ? UNIT_TYPES
          : value.filter((unit) => unit !== ALL_UNIT_TYPES_OPTION)
        : value;

    setFormData((prev) => ({ ...prev, [name]: normalizedValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ✅ FILE
  const handleFileChange = (name, file) => {
    if (!file) return;

    if (name === "brochure" && file.type !== "application/pdf") {
      return setErrors((prev) => ({ ...prev, brochure: "Only PDF allowed" }));
    }

    if (name === "image" && !file.type.startsWith("image/")) {
      return setErrors((prev) => ({ ...prev, image: "Only image files allowed" }));
    }

    handleChange(name, file);
  };

  // ✅ VALIDATE
  const validate = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = "Property name is required";
    if (!formData.city) newErrors.city = "City required";
    if (!formData.area) newErrors.area = "Area required";
    if (!formData.state) newErrors.state = "State required";
    if (!formData.country) newErrors.country = "Country required";
    if (!formData.possession_status) newErrors.possession_status = "Possession status required";

    if (!formData.pincode) {
      newErrors.pincode = "Pincode required";
    } else if (!validatePincode(formData.pincode)) {
      newErrors.pincode = "Invalid pincode";
    }

    if (!formData.property_type)
      newErrors.property_type = "Property type is required";

    if (
      formData.site_property_url &&
      !validateWebsite(formData.site_property_url)
    ) {
      newErrors.site_property_url = "Invalid URL";
    }


    if(formData.property_type !== "plot" && !formData.unit_type.length) {
      newErrors.unit_type = "Unit type required";
    }

    if (!formData.min_budget) newErrors.min_budget = "Min budget required";
    if (!formData.max_budget) newErrors.max_budget = "Max budget required";
    if (!minBudgetUnitType) newErrors.min_budget_unit = "Min budget unit required";
    if (!maxBudgetUnitType) newErrors.max_budget_unit = "Max budget unit required";

    if (
      convertBudgetToNumber(formData.max_budget, maxBudgetUnitType) <= convertBudgetToNumber(formData.min_budget, minBudgetUnitType )
    ) {
      newErrors.max_budget = "Must be greater";
    }

    if (!formData.expected_delivery)
      newErrors.expected_delivery = "Required";

    const deliveryDate = parseDateString(formData.expected_delivery);
    const today = new Date();

    if (
      formData.possession_status === "completed" &&
      deliveryDate &&
      deliveryDate > today
    ) {
      newErrors.expected_delivery = "Expected delivery must be in the past";
    }
    if (!formData.image) newErrors.image = "Image required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ SUBMIT
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const formDataObj = new FormData();

    const formattedExpectedDelivery = formatDateAPIFormat(formData.expected_delivery);

    Object.entries({
      title: formData.name,
      city: formData.city,
      area: formData.area,
      state: formData.state,
      country: formData.country,
      pincode: formData.pincode,
      property_url: formData.site_property_url,
      possession_status: formData.possession_status,
      property_type: formData.property_type,
      // unit_type: formData.unit_type,
      min_budget: convertBudgetToNumber(formData.min_budget, minBudgetUnitType),
      max_budget: convertBudgetToNumber(formData.max_budget, maxBudgetUnitType),
      expected_delivery: formattedExpectedDelivery,
      status: formData.status,
      location:formData.area
    }).forEach(([key, value]) => formDataObj.append(key, value));


    if(formData.property_type !== "plot") {
      formData.unit_type.forEach((unit) => {
        formDataObj.append("unit_type", unit);
      });
    }else{
      formDataObj.append("unit_type", null);
    }

    if (formData.brochure instanceof File)
      formDataObj.append("brochure", formData.brochure);

    if (formData.image instanceof File)
      formDataObj.append("image", formData.image);

    onSubmit(formDataObj);
  };

  // ✅ CANCEL
  const handleCancel = () => {
    setFormData(createInitialFormData());
    setShowCalendar(false);
    setErrors({});
    onCancel();
  };

  return {
    formData,
    errors,
    showCalendar,
    setShowCalendar,
    handleChange,
    handleFileChange,
    handleSubmit,
    handleCancel,
    isEditMode,
    minBudgetUnitType,
    setMinBudgetUnitType,
    maxBudgetUnitType,
    setMaxBudgetUnitType,
  };
};